import * as React from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import Media from 'react-media';

import CheckO from 'crehana-icons/CheckO';
import { greenMain } from 'crehana-ui/styles/colors';
import ShoppingCartPopup from 'Shared/ShoppingCartPopup';
import Dialog from 'crehana-ui/Dialog';
import BasicDataForm from 'Jsx/Auth/components/BasicDataForm';
import useMe from 'Jsx/Shared/Hooks/useMe';

import {
  eventNames,
  facebookTracking,
} from 'Jsx/Utils/facebookTracking/facebookTracking';
import createRecursiveFunctionForAnalytics from 'Utils/createRecursiveFunctionForAnalytics';

import mixpanelTracking from 'Jsx/Utils/mixpanelTracking';
import useUITheme from 'Jsx/Shared/Hooks/useUITheme';

import {
  SNACKBAR_ACTIONS,
  snackbarReducer,
} from './SnackBarConfirmation/reducers';
import SnackBarConfirmation from './SnackBarConfirmation/SnackBarConfirmation';

import { WISHLIST_ACTIONS, wishlistReducer } from './wishlist/reducers';
import {
  SHOPPING_CART_ACTIONS,
  shoppingCartReducer,
} from './shoppingCart/reducers';

/* Dialogs */
import UpSellingV2 from './shoppingCart/components/UpsellingV2';

/* shoppingCart graphql */
import ShoppingCartRemoveItem from './shoppingCart/graphql/ShoppingCartRemoveItem.graphql';
import ShoppingCartAddItem from './shoppingCart/graphql/ShoppingtCartAddItem.graphql';
import CourseQuery from './shoppingCart/graphql/CourseQuery.graphql';
import MyCartQuery from './shoppingCart/graphql/MyCartQuery.graphql';

import DELETE_WISHLIST_ITEM_MUTATION from './wishlist/graphql/deleteWishlistItem.graphql';
import ADD_WISHLIST_ITEM_MUTATION from './wishlist/graphql/addWishlistItem.graphql';
import GET_WISHLIST_QUERY from './wishlist/graphql/getWishlist.graphql';

/* Shoppingcart dependencies */
import { ROOT_ADD_TO_CART, ROOT_REMOVE_FROM_CART } from './shoppingCart/events';
import { parseData } from './shoppingCart/helpers';
import logger from './shoppingCart/logger';

import { PRODUCTS_TRACKS } from './tracks';

import {
  ShoppingCartContextState,
  AddItemMutationType,
} from './shoppingCart/types';
import { ProductsContextType } from './types';
import mixpanel from 'Jsx/Utils/mixpanelTracking';

const SnackbarInitialState = {
  open: false,
  message: '',
};

const wishlistInitialState = {
  items: [],
  lastAction: null,
  preventWishlistChunkToLoad: true,
  fetchingStatus: false,
  /* TODO: Delete this promoPrice 'cause it's a remanent, but make sure every component using ProductsContainer wishliststate
  should not crash */
  promoPrice: null,
};

const shoppingCartInitialState: ShoppingCartContextState = {
  courseIdsInCart: [],
  bundleIdsInCart: [],
  // Fetching cart data loading status
  loadingCart: true,
  // Executing cart mutation status (adding or removing item to the cart)
  loadingCartMutation: false,
  nextDiscount: 0,
  cartSummary: {
    idCart: null,
    totalOff: null,
    dataExtraDiscount: null,
    currencySymbol: null,
    totalPrice: null,
    totalRealPrice: null,
  },
  items: [],
  courses: [],
  bundles: [],
  showShoppingCartPopup: false,
  lastItemAdded: null,
  fetchShoppingCartPopupChunk: false,
  lastItemAddedType: null,
  flagClassicShoppingCart: false,
};

const initialState = {
  wishlistState: wishlistInitialState,
  shoppingCartState: shoppingCartInitialState,
  SnackbarState: SnackbarInitialState,
  /* Wishlist methods */
  toggleWishlistItem: () => {},
  removeFromWishlist: () => {},
  /* Shopping cart methods */
  addCourseToCart: () => Promise.resolve(),
  removeCourseFromCart: () => Promise.resolve(),
  closeShoppingCartPopup: () => {},
  addBundleToCart: () => Promise.resolve(),
  removeBundleFromCart: () => Promise.resolve(),
  setBasicDataForm: () => {},
};

const mainReducer = (
  { wishlistState, shoppingCartState, SnackbarState },
  action,
) => ({
  wishlistState: wishlistReducer(wishlistState, action),
  shoppingCartState: shoppingCartReducer(shoppingCartState, action),
  SnackbarState: snackbarReducer(SnackbarState, action),
});

export const ProductsContext = React.createContext<ProductsContextType>(
  initialState,
);

declare global {
  interface Window {
    showRegisterModalForWishlist(): () => void;
    MIXPANEL_PAGE: string;
  }
}

const getFeatureFlagFromInitialState = featureFlagName => {
  return window.__INITIAL_STATE__ && window.__INITIAL_STATE__.feature_flags
    ? window.__INITIAL_STATE__.feature_flags.find(
        f => f.key === featureFlagName,
      )
    : null;
};

// Called when a product is added/removed to a shopping cart.
export const googleAnalyticsShoppingCart = (courseId, bundleId, action) => {
  createRecursiveFunctionForAnalytics(() => {
    window.ga('create', 'UA-60829200-1', { name: 'crehProductsContainer' });
    window.ga('crehProductsContainer.require', 'ec');

    let product;

    if (courseId) {
      product = {
        originalId: courseId,
        type: 'COURSE',
      };
    } else {
      product = {
        originalId: bundleId,
        type: 'CERTIFICATION',
      };
    }
    window.ga('crehProductsContainer.ec:addProduct', {
      id: `${product.type}_${product.originalId}`,
      quantity: 1,
    });

    if (action === 'add') {
      window.ga('crehProductsContainer.ec:setAction', 'add');
      window.ga(
        'crehProductsContainer.send',
        'event',
        'UX',
        'click',
        'add to cart',
      ); // Send data using an event.
    } else if (action === 'remove') {
      window.ga('crehProductsContainer.ec:setAction', 'remove');
      window.ga(
        'crehProductsContainer.send',
        'event',
        'UX',
        'click',
        'remove from cart',
      ); // Send data using an event.
    }
  })();
};

const ProductsContainer = ({ children, ...props }) => {
  const client = useApolloClient();
  const { me } = useMe();
  const [basicDataForm, setBasicDataForm] = React.useState({
    isOpen: false,
    redirectUrl: null,
  });
  const [
    { wishlistState, shoppingCartState, SnackbarState },
    dispatch,
  ] = React.useReducer(mainReducer, initialState);
  const { isDark } = useUITheme();
  const isDarkMode = isDark && props.darkModeSupported;

  const fetchShoppingCart = () => {
    const flagClassicShoppingCart = window.__INITIAL_STATE__
      ? getFeatureFlagFromInitialState('flag_classic_shopping_cart')
      : true;

    dispatch({
      type: SHOPPING_CART_ACTIONS.SET_FLAG_CLASSIC_SHOPPING_CART,
      status: Boolean(flagClassicShoppingCart),
    });

    dispatch({ type: SHOPPING_CART_ACTIONS.SET_LOADING_CART, status: true });

    return client
      .query({
        query: MyCartQuery,
        fetchPolicy: 'network-only',
      })
      .then(response => {
        dispatch({
          type: SHOPPING_CART_ACTIONS.SET_LOADING_CART,
          status: false,
        });
        if (response.data.myCart) {
          const data = parseData(response.data.myCart);

          dispatch({
            type: SHOPPING_CART_ACTIONS.SET_STATE,
            state: { ...data },
          });
          return data;
        }
      })
      .catch(error => {
        console.log('error to get shoppingtCart', error);
      });
  };

  const addToWishlist = (item, preventSnackbar = false) => {
    if (window.MIXPANEL_PAGE) {
      mixpanelTracking.track(PRODUCTS_TRACKS.WISHLIST_ADD_ITEM, {
        mixpanel_page: window.MIXPANEL_PAGE,
      });
    }
    dispatch({ type: WISHLIST_ACTIONS.ADD_ITEM, item });
    dispatch({
      type: WISHLIST_ACTIONS.SET_PREVENT_SNACKBAR,
      status: preventSnackbar,
    });
    /* Now save it in the backend */
    client.mutate({
      mutation: ADD_WISHLIST_ITEM_MUTATION,
      variables: {
        courseId: item.originalId,
      },
    });
  };

  const removeFromWishlist = (
    item,
    preventPromoDialogShow = false,
    preventSnackbar = false,
  ) => {
    if (window.MIXPANEL_PAGE) {
      mixpanelTracking.track(PRODUCTS_TRACKS.WISHLIST_REMOVE_ITEM, {
        mixpanel_page: window.MIXPANEL_PAGE,
      });
    }

    dispatch({ type: WISHLIST_ACTIONS.DELETE_ITEM, item });
    dispatch({
      type: WISHLIST_ACTIONS.SET_PREVENT_SNACKBAR,
      status: preventSnackbar,
    });
    /* Now save it in the backend */
    client.mutate({
      mutation: DELETE_WISHLIST_ITEM_MUTATION,
      variables: {
        courseId: item.originalId,
      },
    });
  };

  const fetchWishlist = () => {
    return client
      .query({
        query: GET_WISHLIST_QUERY,
        fetchPolicy: 'network-only',
      })
      .then(res => {
        dispatch({
          type: WISHLIST_ACTIONS.SET_ITEMS,
          items: res.data.wishedCourses.map(el => el.course),
        });
        dispatch({
          type: WISHLIST_ACTIONS.SET_WISHLIST_FETCHING_STATUS,
          status: false,
        });
      })
      .catch(error => {
        console.log('Error', error);
      });
  };

  const toggleWishlistItem = (item, preventWishlistDialogToShow = false) => {
    if (
      window.__INITIAL_STATE__ &&
      !window.__INITIAL_STATE__.user &&
      typeof window.showRegisterModalForWishlist !== 'undefined'
    ) {
      window.showRegisterModalForWishlist();
    } else {
      const itemsIds = wishlistState.items.map(el => el.originalId);

      const isInWishlist = itemsIds.indexOf(item.originalId) !== -1;

      const facebookData = {
        content_name: item.title,
        content_ids: [item.originalId],
        content_type: 'product',
        value: (item.prices && item.prices.price) || -1,
        currency: item.prices.currencySymbol,
      };

      if (isInWishlist) {
        removeFromWishlist(item);
      } else {
        facebookTracking(eventNames.ADD_TO_WISH_LIST, facebookData);
        addToWishlist(item);
      }
    }
  };

  /** ********************************** */
  /** ***** Shoppingcart methods ******* */
  /** ********************************** */

  const currencyISOCodes = {
    'S/': 'PEN',
    AR$: 'ARS',
    MX$: 'MXN',
    CO$: 'COP',
    '€': 'EUR',
    CL$: 'CLP',
    US$: 'USD',
    R$: 'BRL',
  };

  const addItemToFacebookTracker = item => {
    const facebookData = {
      // eslint-disable-line no-undef
      content_name: item.title,
      content_ids: [item.originalId],
      content_type: 'product',
      value: item.price,
      currency: currencyISOCodes[item.currencySymbol] || 'USD',
    };

    facebookTracking(eventNames.ADD_TO_CART, facebookData);
  };

  const removeItemMutation = (item): Promise<any> => {
    const { courseId, bundleId } = item;

    dispatch({
      type: SHOPPING_CART_ACTIONS.SET_LOADING_CART_MUTATION,
      status: true,
    });

    const isCourse = !!courseId;
    const isBundle = !!bundleId;

    dispatch({
      type: SNACKBAR_ACTIONS.SET_MESSAGE,
      message: `${
        isCourse ? 'Curso eliminado' : 'Certificación eliminada'
      } del carrito.`,
    });

    mixpanelTracking.track(ROOT_REMOVE_FROM_CART, {
      item_type: isCourse ? 'course' : isBundle ? 'bundle' : 'unknown', //eslint-disable-line
    });

    googleAnalyticsShoppingCart(courseId, bundleId, 'remove');

    return new Promise((resolve, reject) => {
      client
        .mutate({
          mutation: ShoppingCartRemoveItem,
          variables: {
            bundleId,
            courseId,
          },
        })
        .then(
          ({
            data: {
              shoppingCartRemoveItem: { success, shoppingCart },
            },
          }) => {
            dispatch({
              type: SHOPPING_CART_ACTIONS.SET_LOADING_CART_MUTATION,
              status: false,
            });
            if (success) {
              const data = parseData(shoppingCart);

              dispatch({
                type: SHOPPING_CART_ACTIONS.SET_STATE,
                state: { ...data },
              });
              dispatch({
                type: SNACKBAR_ACTIONS.SHOW,
                open: true,
              });
              resolve(shoppingCartState);
            } else {
              logger.warn(
                'ShoppingCartRemoveItem mutation failed. Probably the item is not in the cart',
              );
              reject(shoppingCartState);
            }
          },
        )
        .catch(err => {
          dispatch({
            type: SHOPPING_CART_ACTIONS.SET_LOADING_CART_MUTATION,
            status: false,
          });
          console.error('error to remove item', err);
          reject(shoppingCartState);
        });
    });
  };

  const removeCourseFromCart = (course): Promise<any> => {
    return removeItemMutation(course);
  };

  const addItemMutation: AddItemMutationType = (
    item,
    options = { preventRedirect: false },
  ) => {
    const { courseId, bundleId } = item;
    const { preventRedirect } = options;
    const isCourse = !!courseId;
    const isBundle = !!bundleId;
    const isMobile = window && window.innerWidth < 768;
    const SHOPPING_CART_URL = '/carrito-de-compras/';

    googleAnalyticsShoppingCart(courseId, bundleId, 'add');

    mixpanelTracking.track(ROOT_ADD_TO_CART, {
      item_type: isCourse ? 'course' : isBundle ? 'bundle' : 'unknown', //eslint-disable-line
    });

    return new Promise((resolve, reject) => {
      dispatch({
        type: SHOPPING_CART_ACTIONS.SET_LOADING_CART_MUTATION,
        status: true,
      });
      const { mutate, query } = client;

      dispatch({
        type: SHOPPING_CART_ACTIONS.SET_SHOW_SHOPPING_CART_POPUP,
        visibility: location.pathname !== SHOPPING_CART_URL,
      });
      dispatch({
        type: SHOPPING_CART_ACTIONS.SET_FETCH_SHOPPING_CART_POPUP_CHUNK,
        visibility: location.pathname !== SHOPPING_CART_URL,
      });

      mutate({
        mutation: ShoppingCartAddItem,
        variables: {
          courseId,
          bundleId,
        },
      })
        .then(
          ({
            data: {
              shoppingCartAddItem: { success, shoppingCart },
            },
          }) => {
            if (success) {
              if (typeof window !== 'undefined' && window.fbq && courseId) {
                query({
                  query: CourseQuery,
                  variables: { id: courseId },
                }).then(response => {
                  if (response.data.course) {
                    addItemToFacebookTracker(response.data.course);
                    if (isMobile && !preventRedirect) {
                      setTimeout(() => {
                        window.location.href = SHOPPING_CART_URL;
                      }, 0);
                    }
                  }
                });
              }
              const data = parseData(shoppingCart);

              dispatch({
                type: SHOPPING_CART_ACTIONS.SET_LAST_ITEM_ADDED,
                item: courseId //eslint-disable-line
                  ? data.courses.find(course => course.originalId === courseId)
                  : bundleId
                  ? data.bundles.find(bundle => bundle.originalId === bundleId)
                  : null,
              });

              dispatch({
                type: SHOPPING_CART_ACTIONS.SET_LOADING_CART_MUTATION,
                status: false,
              });
              dispatch({
                type: SHOPPING_CART_ACTIONS.SET_STATE,
                state: { ...data },
              });
              if (location.pathname === SHOPPING_CART_URL) {
                window.scroll(0, 0);
              }
              resolve(shoppingCartState);
            } else {
              dispatch({
                type: SHOPPING_CART_ACTIONS.SET_LOADING_CART_MUTATION,
                status: false,
              });
              reject(success);
              logger.warn(
                'shoppingCartAddItem mutation failed. Probably the item is already in the cart',
              );
            }
          },
        )
        .catch(err => {
          dispatch({
            type: SHOPPING_CART_ACTIONS.SET_LOADING_CART_MUTATION,
            status: false,
          });
          reject(err);
          console.error('error to add item', err);
        });
    });
  };

  const addCourseToCart = (
    course,
    options = { preventRedirect: true },
  ): Promise<any> => {
    const { courseId } = course;
    const wishlistItemsIds = wishlistState.items.map(el => el.originalId);

    if (wishlistItemsIds.indexOf(courseId) !== -1) {
      removeFromWishlist({ originalId: courseId });
    }

    dispatch({
      type: SHOPPING_CART_ACTIONS.SET_LAST_ITEM_ADDED_TYPE,
      itemType: 'course',
    });

    return addItemMutation(course, options);
  };

  const removeBundleFromCart = (bundle): Promise<any> => {
    return removeItemMutation(bundle);
  };

  const closeShoppingCartPopup = () => {
    dispatch({
      type: SHOPPING_CART_ACTIONS.SET_SHOW_SHOPPING_CART_POPUP,
      visibility: false,
    });
    dispatch({ type: SHOPPING_CART_ACTIONS.SET_LAST_ITEM_ADDED, item: null });
    mixpanel.track(mixpanel.EVENTS.CLOSE_DIALOG, {
      dialog: 'Shopping Cart Dialog',
    });
  };

  const addBundleToCart = (bundle: { bundleId: number }): Promise<any> => {
    dispatch({
      type: SHOPPING_CART_ACTIONS.SET_LAST_ITEM_ADDED_TYPE,
      itemType: 'bundle',
    });
    return addItemMutation(bundle);
  };

  /* First render */
  React.useEffect(() => {
    const user = window.__INITIAL_STATE__
      ? window.__INITIAL_STATE__.user
      : null;

    const hasMembership = user && user.isPremiumMember;

    if (!hasMembership) {
      fetchShoppingCart();
    }
    if (user) {
      dispatch({
        type: WISHLIST_ACTIONS.SET_WISHLIST_FETCHING_STATUS,
        status: true,
      });
      fetchWishlist();
    }
  }, []); // eslint-disable-line

  React.useEffect(() => {
    if (wishlistState.lastAction && !wishlistState.preventSnackbar) {
      if (window.openSnackbar) {
        if (wishlistState.lastAction === 'ADDED') {
          window.openSnackbar('Curso agregado a tu lista de deseos', false);
        } else {
          window.openSnackbar('Curso eliminado de tu lista de deseos', false);
        }
      }
    }
  }, [wishlistState.items.length]);

  React.useEffect(() => {
    if (SnackbarState.open) {
      setTimeout(() => {
        dispatch({
          type: SNACKBAR_ACTIONS.SHOW,
          open: false,
        });
      }, 2500);
    }
  }, [SnackbarState.open]);

  const renderSnackBarConfimation = () => {
    const { open, message } = SnackbarState;

    return (
      <Media query="(min-width: 768px)">
        {matches => (
          <SnackBarConfirmation
            open={!matches && open}
            label={message}
            icon={<CheckO size={16} color={greenMain} />}
          />
        )}
      </Media>
    );
  };

  const renderShoppingCartDialog = () => {
    const {
      fetchShoppingCartPopupChunk,
      flagClassicShoppingCart,
      nextDiscount,
    } = shoppingCartState;

    if (fetchShoppingCartPopupChunk) {
      if (!flagClassicShoppingCart) {
        /* Upselling recommendations machine learning driven (Frecuently bought together) */
        return (
          <UpSellingV2
            open={shoppingCartState.showShoppingCartPopup}
            onRequestClose={closeShoppingCartPopup}
            courseIdsInCart={shoppingCartState.courseIdsInCart}
            lastItemAdded={shoppingCartState.lastItemAdded}
            lastItemAddedOriginalId={
              shoppingCartState.lastItemAdded
                ? shoppingCartState.lastItemAdded.originalId
                : -1
            }
            lastItemAddedType={shoppingCartState.lastItemAddedType}
            loading={shoppingCartState.loadingCartMutation}
            totalPriceCart={shoppingCartState.cartSummary.totalPrice}
          />
        );
      }

      return (
        <Media query="(min-width: 768px)">
          {matches => (
            <ShoppingCartPopup
              matches={matches}
              isShoppingcartLoading={shoppingCartState.loadingCartMutation}
              open={shoppingCartState.showShoppingCartPopup}
              onRequestClose={closeShoppingCartPopup}
              nextDiscount={nextDiscount}
              category={
                shoppingCartState.lastItemAdded &&
                shoppingCartState.lastItemAdded.categoryName
              }
              subcategory={
                shoppingCartState.lastItemAdded &&
                shoppingCartState.lastItemAdded.subcategoryName
              }
              isDarkMode={isDarkMode}
            />
          )}
        </Media>
      );
    }

    return <span />;
  };

  const { loadingCartMutation } = shoppingCartState;

  return (
    <ProductsContext.Provider
      value={{
        wishlistState,
        shoppingCartState,
        /* Wishlist methods */
        toggleWishlistItem,
        removeFromWishlist,
        /* Shoppingcart methods */
        loadingCartMutation,
        addCourseToCart,
        removeCourseFromCart,
        addBundleToCart,
        removeBundleFromCart,
        closeShoppingCartPopup,
        setBasicDataForm,
      }}
    >
      {children}
      {/* ShoppingCart Dialog */}
      {renderShoppingCartDialog()}
      {/* SnackBar */}
      {renderSnackBarConfimation()}
      {me && (
        <Dialog
          open={basicDataForm.isOpen}
          size="m"
          onDismiss={() => {
            setBasicDataForm({
              ...basicDataForm,
              isOpen: false,
              redirectUrl: '',
            });
          }}
        >
          <div className="px-24 py-48 sm:px-48 md:px-96 lg:px-112">
            <BasicDataForm
              withInnerPadding
              user={me}
              redirectUrl={basicDataForm.redirectUrl}
            />
          </div>
        </Dialog>
      )}
    </ProductsContext.Provider>
  );
};

export default ProductsContainer;
